import React from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import { useTranslation } from 'react-i18next';
import { FREQUENCIES } from './federationApi';
import Colors from '../../components/ui/Colors';

/** Browser IANA zone, e.g. "America/New_York". */
const browserZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (e) {
    return 'UTC';
  }
};

/** Full IANA zone list when the runtime supports it; else a sensible shortlist. */
const zoneOptions = () => {
  try {
    if (typeof Intl.supportedValuesOf === 'function') {
      const zones = Intl.supportedValuesOf('timeZone');
      if (Array.isArray(zones) && zones.length > 0) return zones;
    }
  } catch (e) {
    /* fall through to shortlist */
  }
  const shortlist = [
    'UTC',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Bogota',
    'America/Sao_Paulo',
    'Europe/London',
    'Europe/Madrid',
    'Europe/Berlin',
    'Asia/Kolkata',
    'Asia/Singapore',
    'Asia/Tokyo',
    'Australia/Sydney',
  ];
  const here = browserZone();
  return shortlist.includes(here) ? shortlist : [here, ...shortlist];
};

/** A fresh, contract-valid default spec (DAILY, browser zone, disabled). */
export const defaultScheduleSpec = () => ({
  frequency: 'DAILY',
  intervalDays: null,
  dayOfWeek: null,
  dayOfMonth: null,
  timeOfDay: '02:00',
  timezone: browserZone(),
  enabled: true,
});

/** Client-side validation mirroring the backend rules → array of i18n error keys. */
const validateSpec = (spec) => {
  const errors = [];
  if (!spec.frequency) errors.push('Federation.schedule.errors.frequencyRequired');
  if (spec.frequency === 'WEEKLY' && !(spec.dayOfWeek >= 1 && spec.dayOfWeek <= 7)) {
    errors.push('Federation.schedule.errors.dayOfWeek');
  }
  if (spec.frequency === 'MONTHLY' && !(spec.dayOfMonth >= 1 && spec.dayOfMonth <= 31)) {
    errors.push('Federation.schedule.errors.dayOfMonth');
  }
  if (spec.frequency === 'EVERY_N_DAYS' && !(spec.intervalDays >= 1)) {
    errors.push('Federation.schedule.errors.intervalDays');
  }
  if (!spec.timeOfDay || !/^\d{2}:\d{2}$/.test(spec.timeOfDay)) {
    errors.push('Federation.schedule.errors.timeOfDay');
  }
  if (!spec.timezone) errors.push('Federation.schedule.errors.timezone');
  return errors;
};

/**
 * Frequency + dependent-field + time + timezone picker with a plain-language
 * "next run" preview. Shared by the onboarding wizard's schedule step and the
 * management screen's edit-schedule dialog.
 *
 * Props:
 *  - initialSpec:   ScheduleSpecDto to seed the form (defaults to DAILY/browser zone).
 *  - onSubmit:      (spec) => void — invoked with a validated request spec.
 *  - submitting:    boolean — disables inputs / shows spinner.
 *  - submitLabel:   button text.
 *  - serverPreview: backend-computed nextRunPreview string (shown after a PUT).
 *  - serverNextRunAt: backend-computed ISO instant (shown after a PUT).
 */
export default function SchedulePicker({
  initialSpec,
  onSubmit,
  submitting = false,
  submitLabel,
  serverPreview,
  serverNextRunAt,
}) {
  const { t } = useTranslation();
  const [spec, setSpec] = React.useState(() => ({ ...defaultScheduleSpec(), ...(initialSpec || {}) }));
  const [touched, setTouched] = React.useState(false);

  const zones = React.useMemo(zoneOptions, []);
  const dayOfWeekLabels = t('Federation.schedule.daysOfWeek', { returnObjects: true });
  const errors = validateSpec(spec);

  const update = (patch) => setSpec((prev) => ({ ...prev, ...patch }));

  // Egress/latency caution (design §10): full-refresh-each-run multiplies provider
  // egress by fetch frequency, so a frequent cadence on a large bucket can be costly.
  // The shortest selectable cadences are DAILY and EVERY_N_DAYS with a tiny interval.
  const frequentCadence =
    spec.frequency === 'DAILY' ||
    (spec.frequency === 'EVERY_N_DAYS' && Number(spec.intervalDays) >= 1 && Number(spec.intervalDays) <= 2);

  const handleFrequencyChange = (frequency) => {
    // Reset dependent fields so a stale value from another frequency never leaks
    // into the request, and seed the newly-relevant one with a valid default.
    update({
      frequency,
      intervalDays: frequency === 'EVERY_N_DAYS' ? spec.intervalDays || 7 : null,
      dayOfWeek: frequency === 'WEEKLY' ? spec.dayOfWeek || 1 : null,
      dayOfMonth: frequency === 'MONTHLY' ? spec.dayOfMonth || 1 : null,
    });
  };

  // Plain-language description built from the spec (the exact next occurrence
  // comes back from the backend on PUT as serverPreview / serverNextRunAt).
  const clientPreview = React.useMemo(() => {
    if (errors.length > 0) return null;
    const days = Array.isArray(dayOfWeekLabels) ? dayOfWeekLabels : [];
    let cadence;
    switch (spec.frequency) {
      case 'DAILY':
        cadence = t('Federation.schedule.preview.daily');
        break;
      case 'WEEKLY':
        cadence = t('Federation.schedule.preview.weekly', {
          day: days[(spec.dayOfWeek || 1) - 1] || `#${spec.dayOfWeek}`,
        });
        break;
      case 'MONTHLY':
        cadence = t('Federation.schedule.preview.monthly', { day: spec.dayOfMonth });
        break;
      case 'EVERY_N_DAYS':
        cadence = t('Federation.schedule.preview.everyNDays', { n: spec.intervalDays });
        break;
      default:
        cadence = '';
    }
    return t('Federation.schedule.preview.line', {
      cadence,
      time: spec.timeOfDay,
      timezone: spec.timezone,
    });
  }, [spec, errors.length, dayOfWeekLabels, t]);

  const handleSubmit = () => {
    setTouched(true);
    if (errors.length > 0) return;
    // Send request fields only; nextRunAt/nextRunPreview are out-only (ignored on input).
    onSubmit({
      frequency: spec.frequency,
      intervalDays: spec.frequency === 'EVERY_N_DAYS' ? Number(spec.intervalDays) : null,
      dayOfWeek: spec.frequency === 'WEEKLY' ? Number(spec.dayOfWeek) : null,
      dayOfMonth: spec.frequency === 'MONTHLY' ? Number(spec.dayOfMonth) : null,
      timeOfDay: spec.timeOfDay,
      timezone: spec.timezone,
      enabled: true,
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        select
        label={t('Federation.schedule.frequency')}
        value={spec.frequency}
        onChange={(e) => handleFrequencyChange(e.target.value)}
        disabled={submitting}
        fullWidth
      >
        {FREQUENCIES.map((f) => (
          <MenuItem key={f} value={f}>
            {t(`Federation.frequency.${f}`)}
          </MenuItem>
        ))}
      </TextField>

      {spec.frequency === 'WEEKLY' && (
        <TextField
          select
          label={t('Federation.schedule.dayOfWeek')}
          value={spec.dayOfWeek || 1}
          onChange={(e) => update({ dayOfWeek: Number(e.target.value) })}
          disabled={submitting}
          fullWidth
        >
          {[1, 2, 3, 4, 5, 6, 7].map((d) => (
            <MenuItem key={d} value={d}>
              {(Array.isArray(dayOfWeekLabels) && dayOfWeekLabels[d - 1]) || `#${d}`}
            </MenuItem>
          ))}
        </TextField>
      )}

      {spec.frequency === 'MONTHLY' && (
        <TextField
          select
          label={t('Federation.schedule.dayOfMonth')}
          value={spec.dayOfMonth || 1}
          onChange={(e) => update({ dayOfMonth: Number(e.target.value) })}
          disabled={submitting}
          fullWidth
          helperText={t('Federation.schedule.dayOfMonthHelp')}
        >
          {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
            <MenuItem key={d} value={d}>
              {d}
            </MenuItem>
          ))}
        </TextField>
      )}

      {spec.frequency === 'EVERY_N_DAYS' && (
        <TextField
          type="number"
          label={t('Federation.schedule.intervalDays')}
          value={spec.intervalDays || ''}
          onChange={(e) => update({ intervalDays: e.target.value === '' ? null : Number(e.target.value) })}
          disabled={submitting}
          inputProps={{ min: 1 }}
          fullWidth
        />
      )}

      <TextField
        type="time"
        label={t('Federation.schedule.timeOfDay')}
        value={spec.timeOfDay}
        onChange={(e) => update({ timeOfDay: e.target.value })}
        disabled={submitting}
        InputLabelProps={{ shrink: true }}
        inputProps={{ step: 60 }}
        fullWidth
      />

      <TextField
        select
        label={t('Federation.schedule.timezone')}
        value={spec.timezone}
        onChange={(e) => update({ timezone: e.target.value })}
        disabled={submitting}
        fullWidth
        SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 320 } } } }}
      >
        {zones.map((z) => (
          <MenuItem key={z} value={z}>
            {z}
          </MenuItem>
        ))}
      </TextField>

      {/* Next-run preview: client-side description, replaced by the backend's
          exact computation once a PUT round-trips. */}
      {(serverPreview || clientPreview) && (
        <Alert icon={<EventRepeatIcon fontSize="inherit" />} severity="info" sx={{ alignItems: 'center' }}>
          <Typography variant="body2">{serverPreview || clientPreview}</Typography>
          {serverNextRunAt && (
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
              {t('Federation.schedule.nextRunAt', { when: new Date(serverNextRunAt).toLocaleString() })}
            </Typography>
          )}
        </Alert>
      )}

      {frequentCadence && (
        <Alert severity="warning" sx={{ alignItems: 'center' }}>
          <Typography variant="body2">{t('Federation.schedule.egressWarning')}</Typography>
        </Alert>
      )}

      {touched && errors.length > 0 && (
        <Alert severity="warning">
          {errors.map((key) => (
            <Typography key={key} variant="caption" sx={{ display: 'block' }}>
              {t(key)}
            </Typography>
          ))}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || errors.length > 0}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <EventRepeatIcon />}
          sx={{ backgroundColor: Colors.secondary, '&:hover': { backgroundColor: Colors.hover } }}
        >
          {submitLabel || t('Federation.schedule.save')}
        </Button>
      </Box>
    </Box>
  );
}
